import {
	AbstractProduct,
	ClientType,
	ProductWithRequired,
	RelatedProducts,
	getClientTypeByInn,
} from 'Pages/orders_design'
import { intersection, isEqual } from 'lodash'
import { ProductGroup, ProductGroupTranslate } from 'Constants/ProductGroup'
import { FieldNames } from 'Constants/FieldNames'
import { ProductModel } from 'Pages/orders'
import { ProductRecommendationModel } from 'Models'

export const filterProducts = <T extends Pick<ProductModel, 'name' | 'available_lk_neo'>>(
	query: string,
	products: T[],
	flag?: keyof T
): T[] =>
	products?.filter(
		(product): boolean =>
			product.name.toLowerCase().includes(query.toLowerCase()) &&
			(!flag || Boolean(product[flag])) &&
			product.available_lk_neo
	)

export const isLockedByClientType = <T extends AbstractProduct>(
	product: T,
	clientType: ClientType
) =>
	!product?.client_type || product.client_type === ClientType.ALL || clientType === ClientType.ALL
		? false
		: product.client_type !== clientType

export const deleteLockedProductsByInn = <T extends AbstractProduct>(
	products: T[],
	selectedIds: Set<number>,
	inn: string
): number[] | null => {
	const clientType = getClientTypeByInn(inn)
	const productIds = products
		?.filter((item) => selectedIds.has(item.id) && !isLockedByClientType(item, clientType))
		.map((item) => item.id)
	return isEqual(selectedIds, new Set(productIds)) ? null : productIds
}

export class ProductsCore {
	private products: ProductModel[]
	private recommendations: ProductRecommendationModel[]

	public constructor(products: ProductModel[], recommendations?: ProductRecommendationModel[]) {
		this.products = products
		this.recommendations = recommendations
	}

	public getRequiredProducts = (
		ids: Set<number>,
		ignoreGroups: string[] = []
	): Map<number, ProductModel[]> => {
		const selectedProducts = new Map()
		ids.forEach((id): void => {
			const product = this.products.find((p): boolean => p.id === id)
			if (
				!ignoreGroups.includes(product?.product_group?.slug) &&
				product?.required_products?.length
			) {
				if (!selectedProducts.has(id)) selectedProducts.set(id, [])
				selectedProducts.set(id, [...selectedProducts.get(id), ...product.required_products])
			}
		})
		return selectedProducts
	}

	public getSelectedWithRequired = (selectedProducts: Set<number>): ProductWithRequired[] => {
		const result = []
		selectedProducts.forEach((id): void => {
			this.products.forEach((product): void => {
				let required = this.getRequiredProducts(selectedProducts).get(product?.id)
				required?.forEach((req): void => {
					if (selectedProducts.has(req?.id)) {
						required = []
					}
				})

				if (id === product?.id) {
					result.push({ product, required })
				}
			})
		})
		return result
	}

	public getRelatedProducts = (selectedProducts: Set<number>): RelatedProducts => {
		const related: RelatedProducts = {
			byProductGroup: new Set<string>(),
			byBlocked: new Set<number>(),
		}
		selectedProducts.forEach((id): void => {
			const product = this.products.find((p): boolean => p.id === id)
			this.products.forEach((dictProduct): void => {
				if (
					product?.product_group?.slug &&
					product.product_group.slug === dictProduct?.product_group?.slug
				) {
					related.byProductGroup.add(product?.product_group?.slug)
				}
				if (
					product?.block_product_group_ids.length &&
					isEqual(product?.block_product_group_ids, dictProduct?.block_product_group_ids)
				) {
					product.block_product_group_ids.forEach((item): void => {
						related.byBlocked.add(Number(item))
					})
				}
			})
		})
		return related
	}

	public groupProducts = (products): Map<string, ProductModel[]> => {
		const groups: Map<string, ProductModel[]> = new Map()
		if (products?.length) {
			const sortedProducts = this.sortProducts([...products])
			sortedProducts.forEach((product: ProductModel): any => {
				const groupName = product.show_name || ProductGroupTranslate[ProductGroup.OTHER]
				const productsInGroup = groups.get(groupName) || []
				groups.set(groupName, [...productsInGroup, product])
			})
		}
		return groups
	}

	public sortProducts = (products: ProductModel[]): ProductModel[] =>
		products.sort(
			(a, b): number =>
				a[FieldNames.PRIORITY_GROUP] - b[FieldNames.PRIORITY_GROUP] ||
				a[FieldNames.SHOW_NAME].localeCompare(b[FieldNames.SHOW_NAME]) ||
				a[FieldNames.PRIORITY] - b[FieldNames.PRIORITY] ||
				a[FieldNames.NAME].localeCompare(b[FieldNames.NAME])
		)

	private getGroupFromProductId = (id: number): string =>
		this.products.find((item): boolean => item.id === id)?.product_group?.slug

	private getProductIdsWithProductGroups = (slug: string): number[] =>
		this.products
			.filter((item): boolean => item.product_group?.slug === slug)
			.map((i): number => i.id)

	public getRecommendationId = (productId: number): number | string =>
		this.recommendations?.find((item): boolean => item.id === productId)?.recommendations[0]?.id

	public getProductById = (selectedProducts: Set<number>, item: ProductModel): ProductModel => {
		return this.products
			.filter((product): boolean => selectedProducts.has(product.id))
			.find((product): boolean => product?.product_group?.id === item?.product_group?.id)
	}
	public addSelectedProduct = (id: number, selectedProducts: Set<number>): Set<number> => {
		const product = this.products.find((p): boolean => p.id === id)
		const group = product?.product_group?.slug
		const blockGroupIds = product?.block_product_group_ids
		const relatedProducts = this.getRelatedProducts(selectedProducts)
		if (relatedProducts.byProductGroup.has(group)) {
			this.products.forEach((pr): void => {
				if (pr?.product_group?.slug === group) {
					selectedProducts.delete(pr?.id)
				}
			})
		}
		if (intersection(Array.from(relatedProducts.byBlocked), blockGroupIds?.map(Number)).length) {
			this.products.forEach((pr): void => {
				if (intersection(pr?.block_product_group_ids, blockGroupIds).length) {
					selectedProducts.delete(pr?.id)
				}
			})
		}
		return new Set(selectedProducts).add(id)
	}

	private clearRecommendations = (
		id: number,
		recommendations: { [key: string]: number }
	): { [key: string]: number } => {
		const newRecommendations = { ...recommendations }
		const productGroup = this.getGroupFromProductId(id)
		const productsIdsWithProductGroup = this.getProductIdsWithProductGroups(productGroup)
		productsIdsWithProductGroup.forEach((i): boolean => delete newRecommendations[i])
		return newRecommendations
	}

	public addRecommendation = (
		id: number,
		recommendations: { [key: string]: number }
	): { [key: string]: number } => {
		const newRecommendations = this.clearRecommendations(id, recommendations)
		const recommendationId = this.getRecommendationId(id)
		return { ...newRecommendations, [id]: recommendationId }
	}

	public removeRecommendation = (
		id: number,
		recommendations: { [key: string]: number }
	): { [key: string]: number } => {
		const newRecommendations = this.clearRecommendations(id, recommendations)
		return { ...newRecommendations }
	}

	public hasProductInSelected = (productSlug: string, selectedProducts: Set<number>): boolean => {
		const product = this.products.find((p): boolean => p?.slug === productSlug)
		return selectedProducts.has(product?.id)
	}
	public hasProductFromProductGroup = (
		productGroup: string,
		selectedProducts: Set<number>
	): boolean => {
		const filteredProducts = this.products.filter(
			(p): boolean => p?.product_group?.slug === productGroup
		)
		return !!intersection(
			[...selectedProducts],
			filteredProducts.map((item): number => item.id)
		).length
	}
}
